import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { EventChips, EventCount } from '@/components/Events'
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from '@/components/icons'
import type { ShownEvent } from '@/hooks/useEvents'
import { fmtDate, isoWeekday } from '@/logic/dates'
import { dayHeading, fmtShort } from '@/logic/labels'
import { openCount, type PlanItem } from '@/logic/plan'
import { usePrefs } from '@/store/prefs'
import { openQuickAdd, toggleEventDay } from '@/store/ui'
import type { DateStr } from '@/types'
import { PlanCard, type CardContext } from './PlanCard'

export interface DayData {
  date: DateStr
  items: PlanItem[]
  events: ShownEvent[]
  /** Whether the day's events are opened as a line of chips. */
  eventsOpen: boolean
  /** Events on Show all: every day's are open, and the count is not a switch. */
  eventsFixed: boolean
}

interface DayProps {
  day: DayData
  today: DateStr
  ctx: CardContext
  over: boolean
  /** Wide columns drop the month: "Today, Wed 14". */
  short?: boolean
  /** Stacked days fold shut like a toggle list. */
  collapsible?: boolean
}

function addFor(date: DateStr) {
  openQuickAdd({ plannedDate: date, hint: `Add for ${fmtDate(date)}` })
}

function DayHead({ day, today, ctx, short, collapsible }: Omit<DayProps, 'over'>) {
  const collapsed = usePrefs((s) => collapsible && s.collapsedWeekdays.includes(isoWeekday(day.date)))
  const toggle = usePrefs((s) => s.toggleWeekday)
  const count = openCount(day.items)
  const title = dayHeading(day.date, today, short)
  const label = (
    <>
      {collapsible && (collapsed ? <ChevronRightIcon size={15} weight={2.4} /> : <ChevronDownIcon size={15} weight={2.4} />)}
      <span className="day__title">{title}</span>
      {!ctx.readOnly && <span className="day__count">{count}</span>}
    </>
  )
  return (
    <div className="day__head">
      {collapsible ? (
        <button type="button" className="day__toggle" aria-expanded={!collapsed} onClick={() => toggle(isoWeekday(day.date))}>
          {label}
        </button>
      ) : (
        <h2 className="day__toggle">{label}</h2>
      )}
      <span className="day__spacer" />
      {!collapsed && <EventCount events={day.events} open={day.eventsOpen} fixed={day.eventsFixed} onToggle={() => toggleEventDay(day.date)} />}
      {!ctx.readOnly && (
        <button type="button" className="icon-btn day__add" aria-label={`Add a task for ${fmtDate(day.date)}`} onClick={() => addFor(day.date)}>
          <PlusIcon size={18} weight={2.2} />
        </button>
      )}
    </div>
  )
}

function DayList({ day, ctx }: { day: DayData; ctx: CardContext }) {
  const live = day.items.filter((i) => !i.done && !i.faint).map((i) => i.task.id)
  if (!day.items.length) return <p className="day__empty">{ctx.readOnly ? 'Nothing done' : 'Nothing planned'}</p>
  return (
    <SortableContext items={live} strategy={verticalListSortingStrategy}>
      <div className="plist">
        {day.items.map((item) => (
          <PlanCard key={item.key} item={item} where={{ day: day.date }} ctx={ctx} container={`day:${day.date}`} />
        ))}
      </div>
    </SortableContext>
  )
}

/** A day in Stacked: a heading that sticks while you scroll through it, then its events and tasks. */
export function DaySection({ day, today, ctx, over }: DayProps) {
  const collapsed = usePrefs((s) => s.collapsedWeekdays.includes(isoWeekday(day.date)))
  const { setNodeRef } = useDroppable({ id: `day:${day.date}`, data: { container: `day:${day.date}` }, disabled: ctx.readOnly })
  return (
    <section ref={setNodeRef} className={`day ${day.date === today ? 'day--today' : ''} ${over ? 'drop-hot' : ''}`} aria-label={fmtDate(day.date)} data-day={day.date}>
      <DayHead day={day} today={today} ctx={ctx} collapsible />
      {!collapsed && (
        <>
          {day.eventsOpen && <EventChips events={day.events} />}
          <DayList day={day} ctx={ctx} />
        </>
      )}
    </section>
  )
}

/** A day in Columns: its own box that scrolls on its own. */
export function DayColumn({ day, today, ctx, over, short }: DayProps) {
  const { setNodeRef } = useDroppable({ id: `day:${day.date}`, data: { container: `day:${day.date}` }, disabled: ctx.readOnly })
  return (
    <section ref={setNodeRef} className={`dcol ${day.date === today ? 'dcol--today' : ''} ${over ? 'drop-hot' : ''}`} aria-label={fmtDate(day.date)} data-day={day.date} id={`dcol-${day.date}`}>
      <DayHead day={day} today={today} ctx={ctx} short={short} />
      {day.eventsOpen && <EventChips events={day.events} />}
      <div className="dcol__body">
        <DayList day={day} ctx={ctx} />
      </div>
    </section>
  )
}

/** A wide column folded to a narrow strip: click to open; a dragged card held over it springs it open. */
export function DayStrip({ day, ctx, over }: { day: DayData; ctx: CardContext; over: boolean }) {
  const open = usePrefs((s) => s.openWeekday)
  const { setNodeRef } = useDroppable({ id: `day:${day.date}`, data: { container: `day:${day.date}` }, disabled: ctx.readOnly })
  return (
    <section ref={setNodeRef} className={`dstrip ${over ? 'drop-hot' : ''}`} aria-label={`${fmtDate(day.date)}, folded`} data-day={day.date}>
      <button type="button" className="icon-btn" aria-label={`Open ${fmtDate(day.date)}`} onClick={() => open(isoWeekday(day.date))}>
        <ChevronRightIcon size={18} weight={2.4} />
      </button>
      <span className="dstrip__label">
        {fmtShort(day.date)} · {ctx.readOnly ? day.items.length : openCount(day.items)}
      </span>
    </section>
  )
}
